// import { unansweredFeedbacks } from "./pages/mock";

type GenericResponse = {
  message: string;
  statusCode: number;
  error: string;
};

export type Marketplace = 'wb' | 'ozon';

export type Store = {
  id: string;
  name: string;
  token?: string; // для WB
  clientId?: string; // для Ozon
  apiKey?: string; // для Ozon
  prompt: Record<string, any>;
  generateEmptyFeedback: boolean;
  smartAutoReplies?: boolean;
  marketplace: Marketplace;
};

export type Credentials = {
  email: string;
  password: string;
};

export type RegCredentials = Credentials & {
  name: string;
  secret: string;
};

export type PublishResult = {
  id: string;
  published: boolean;
  message?: string;
};

type ProductDetails = {
  imtId: number;
  nmId: number;
  productName: string;
  supplierArticle: string;
  supplierName: string;
  brandName: string;
  size: string;
};

export type Feedback = {
  id: string;
  text: string;
  pros?: string;
  cons?: string;
  rating: number;
  productValuation?: number;
  createdDate: string;
  author: string;
  userName?: string;
  productName: string;
  productId: string | number;
  productDetails?: {
    productName: string;
    nmId: string | number;
  };

  // Новые поля для маркетплейсов
  marketplace?: Marketplace;

  // Специфичные для WB поля
  wb_nmId?: number;     // Номенклатурный номер WB (для ссылок)
  wb_imtId?: number;    // ID модели товара WB

  // Специфичные для Ozon поля
  ozon_productId?: number; // ID товара в Ozon
  ozon_sku?: number;      // SKU в Ozon (для ссылок)

  // ID для формирования ссылки (wb_nmId или ozon_sku)
  linkId?: number;

  // Обратная совместимость
  sku?: number;

  answer?: {
    text: string;
    createdDate: string;
  };
  photos?: string[];
  canAnswer: boolean;
  state: string;
  comments?: FeedbackComment[];
  commentsAmount?: number;
  hasText: boolean;
};

export type FeedbackComment = {
  id: string;
  text: string;
  createdDate?: string;
  isFromSeller?: boolean;
};

export type Question = {
  id: string;
  text: string;
  userName: string;
  createdDate: string; // ISO date string
  answer: string | null;
  productId: string;
  productName: string;
  sku?: number; // для Ozon

  // Новые поля для маркетплейсов
  marketplace?: Marketplace;

  // Специфичные для WB поля
  wb_nmId?: number;     // Номенклатурный номер WB
  wb_imtId?: number;    // ID модели товара WB

  // Специфичные для Ozon поля
  ozon_productId?: number; // ID товара в Ozon
  ozon_sku?: number;      // SKU в Ozon

  // ID для формирования ссылки (wb_nmId или ozon_sku)
  linkId?: number | string;
};

export type QuestionResponse = {
  data: {
    countUnanswered: number;
    countTotal: number;
    questions: Question[];
  };
  error: boolean;
  errorText: string;
  additionalErrors: any | null;
};

export type FeedbackResponse = {
  data: {
    countUnanswered: number;
    countArchive: number;
    feedbacks: Feedback[];
  };
  error: boolean;
  errorText: string;
  additionalErrors: any | null;
};

export type User = {
  id: string;
  email: string;
  name: string;
  tokenBalance: number;
  type: "user" | "admin";
};

export type UserBalance = { id: string; tokenBalance: number };

export type AdminSettings = {
  secret: string;
  adminLink: string;
  showAdminLink: boolean;
};

export type Reply = { id: string; text: string; isAllowed: boolean };

export type WbFeedbackForApi = {
  id: string;
  text: string;
  pros: string;
  cons: string;
  customerName: string;
  productValuation: number;
  productName: string;
  marketplace: 'wb';
};

export type OzonFeedbackForApi = {
  id: string;
  text: string;
  customerName: string;
  productValuation: number;
  marketplace: 'ozon';
};

export type FeedbackForApi = WbFeedbackForApi | OzonFeedbackForApi;

export async function makeRequest<T>(url: string, opts?: RequestInit): Promise<T> {
  // Определяем базовый URL
  let baseUrl = window.location.origin;

  // Если мы на http и домен не localhost, принудительно используем https
  if (window.location.protocol === 'http:' &&
    !window.location.hostname.includes('localhost') &&
    !window.location.hostname.includes('127.0.0.1')) {
    baseUrl = baseUrl.replace('http:', 'https:');
  }

  const token = localStorage.getItem("jwt");
  const isAuthEndpoint = url.includes('/api/auth/');

  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  // Добавляем заголовок авторизации для всех неаутентификационных запросов
  if (!isAuthEndpoint && token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  console.log('Making request:', {
    url: baseUrl + url,
    method: opts?.method || 'GET',
    headers: {
      ...headers,
      ...(opts?.headers || {})
    },
    body: opts?.body ? JSON.parse(opts.body as string) : undefined
  });

  const response = await fetch(baseUrl + url, {
    ...opts,
    headers: {
      ...headers,
      ...(opts?.headers || {}),
    },
  });

  // Клонируем ответ для логирования
  const clonedResponse = response.clone();
  
  try {
    const responseData = await response.json();
    
    if (!response.ok) {
      console.error("API Error response:", {
        status: response.status,
        statusText: response.statusText,
        data: responseData
      });

      // Обработка ошибок аутентификации
      if (response.status === 401 && !isAuthEndpoint) {
        console.error('Authentication error detected, logging out');
        localStorage.removeItem("jwt");
        window.location.replace("/login");
      } else if (response.status === 403 && !isAuthEndpoint) {
        if (url.includes('/api/store') && opts?.method === 'PATCH') {
          console.error('Permission denied when updating store');
          alert('Ошибка доступа при обновлении магазина. Возможно, у вас нет прав на редактирование этого магазина или произошла ошибка сервера.');
        } else if (responseData.message?.toLowerCase().includes('unauthorized') ||
          responseData.message?.toLowerCase().includes('forbidden') ||
          responseData.message?.toLowerCase().includes('jwt')) {
          console.error('Authentication error detected, logging out');
          localStorage.removeItem("jwt");
          window.location.replace("/login");
        } else {
          alert(responseData.message || 'Ошибка доступа. У вас может не быть прав для выполнения этого действия.');
        }
      }

      throw new Error(responseData.message || "API Error");
    }
    
    return responseData;
  } catch (error) {
    console.error("Error handling API response:", error);
    
    // Логируем детали запроса при ошибке
    console.error("Request details:", {
      url: baseUrl + url,
      method: opts?.method || "GET",
      headers: {
        ...headers,
        ...(opts?.headers || {}),
      },
      body: opts?.body ? JSON.parse(opts.body as string) : undefined
    });
    
    // Пытаемся прочитать тело ответа для дополнительной диагностики
    try {
      const errorResponseText = await clonedResponse.text();
      console.error("Error response body:", errorResponseText);
    } catch (e) {
      console.error("Could not read error response body:", e);
    }
    
    throw error;
  }
}

export async function getStoreList(marketplace?: Marketplace) {
  const url = marketplace
    ? `/api/store/list?marketplace=${marketplace}`
    : "/api/store/list";
  return await makeRequest<Store[]>(url);
}

export async function getStoreById(id: string) {
  return await makeRequest<Store>(`/api/store/${id}`);
}

export async function getStoreByIdForUser(storeId: string) {
  return makeRequest<{ id: string; marketplace: string }>(`/api/store/${storeId}`);
}

export async function getUnanswereFeedbacks({
  page,
  perPage,
  storeId,
  hasTextOnly,
  includeComments,
}: {
  page: number;
  perPage: number;
  storeId: string;
  hasTextOnly?: boolean;
  includeComments?: boolean;
}) {
  // Убираем ограничение на количество отзывов
  let url = `/api/feedback/unanswered/list?page=${page}&perPage=${perPage}&storeId=${storeId}`;

  if (hasTextOnly) {
    url += '&hasTextOnly=true';
  }

  if (includeComments) {
    url += '&includeComments=true';
  }

  return await makeRequest<FeedbackResponse>(url);
}

export async function login(credentials: Credentials) {
  return makeRequest<{ access_token: string }>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify(credentials),
  });
}

export async function registration(credentials: RegCredentials) {
  return makeRequest<User>("/api/auth/register", {
    method: "POST",
    body: JSON.stringify(credentials),
  });
}

export async function logout() {
  localStorage.removeItem("jwt");
  window.location.replace("/login");
}

export async function getUserInfo() {
  return makeRequest<User>("/api/user/info");
}

export async function getUserList() {
  return makeRequest<User[]>("/api/user/list");
}

export async function changeUserBalance(body: UserBalance) {
  return makeRequest<UserBalance>("/api/user/balance", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function deleteUser(id: string) {
  return makeRequest<User>(`/api/user/${id}`, {
    method: "DELETE",
  });
}

export async function getAdminSettings() {
  return makeRequest<AdminSettings>("/api/admin/settings");
}

export async function updateAdminSettings(body: AdminSettings) {
  return makeRequest<AdminSettings>("/api/admin/settings", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function updateStore(body: {
  id: string;
  name: string;
  prompt: Record<string, any>;
  token?: string; // для WB
  clientId?: string; // для Ozon
  apiKey?: string; // для Ozon
  marketplace?: Marketplace;
  generateEmptyFeedback?: boolean;
}) {
  console.log('Updating store with data:', { ...body, apiKey: body.apiKey ? '[HIDDEN]' : undefined });

  if (!body.marketplace) {
    throw new Error('Marketplace is required');
  }

  // Проверяем и формируем данные в зависимости от типа магазина
  if (body.marketplace === 'wb') {
    if (!body.token) {
      throw new Error('Token is required for WB store');
    }
    return makeRequest<Store>(`/api/store/wb/${body.id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: body.name,
        token: body.token,
        prompt: body.prompt,
        generateEmptyFeedback: body.generateEmptyFeedback,
        marketplace: 'wb' as const,
      }),
    });
  } else if (body.marketplace === 'ozon') {
    if (!body.clientId || !body.apiKey) {
      throw new Error('ClientId and ApiKey are required for Ozon store');
    }
    return makeRequest<Store>(`/api/store/ozon/${body.id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: body.name,
        clientId: body.clientId,
        apiKey: body.apiKey,
        prompt: body.prompt,
        generateEmptyFeedback: body.generateEmptyFeedback,
        marketplace: 'ozon' as const,
      }),
    });
  }

  throw new Error('Invalid marketplace type');
}

export async function createStore(body: {
  name: string;
  prompt: Record<string, any>;
  token?: string; // для WB
  clientId?: string; // для Ozon
  apiKey?: string; // для Ozon
  marketplace: Marketplace;
  generateEmptyFeedback?: boolean;
  smartAutoReplies?: boolean;
}) {
  return makeRequest<Store>("/api/store/create", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
}

export async function deleteStore(id: string) {
  return makeRequest(`/api/store/${id}`, {
    method: "DELETE",
  });
}

export async function checkWBToken(token: string) {
  return makeRequest<{ valid: boolean }>(`/api/store/wb/token/check`, {
    body: JSON.stringify({ token }),
    method: "POST",
  });
}

export async function checkOzonCredentials(credentials: { clientId: string; apiKey: string }) {
  return makeRequest<{ valid: boolean }>(`/api/store/ozon/credentials/check`, {
    body: JSON.stringify(credentials),
    method: "POST",
  });
}

export async function getGeneratedReply({
  feedbacks,
  storeId,
}: {
  feedbacks: FeedbackForApi[];
  storeId: string;
}) {
  return makeRequest<Reply[]>(`/api/feedback/generate/list`, {
    body: JSON.stringify({
      feedbacks,
      storeId,
    }),
    method: "POST",
  });
}

export async function publishWBReplies({
  replies,
  storeId,
  state,
}: {
  replies: {
    id: string;
    text: string;
  }[];
  storeId: string;
  state?: string;
}) {
  // Создаем объект для тела запроса
  const requestBody: any = {
    replies,
    storeId,
  };

  // Добавляем state только если он передан
  if (state) {
    requestBody.state = state;
  }

  return makeRequest<PublishResult[]>(`/api/feedback/replies/publish/wb`, {
    body: JSON.stringify(requestBody),
    method: "POST",
  });
}

export async function publishOzonReplies({
  replies,
  storeId,
}: {
  replies: {
    id: string;
    text: string;
  }[];
  storeId: string;
}) {
  return makeRequest<PublishResult[]>(`/api/feedback/replies/publish/ozon`, {
    body: JSON.stringify({
      replies,
      storeId,
    }),
    method: "POST",
  });
}

export async function publishReplies({
  replies,
  storeId,
}: {
  replies: {
    id: string;
    text: string;
  }[];
  storeId: string;
}) {
  const store = await getStoreByIdForUser(storeId);
  return store.marketplace === 'wb' ? publishWBReplies({ replies, storeId }) : publishOzonReplies({ replies, storeId });
}

export async function getCachedReplies(ids: string[]) {
  return makeRequest<Reply[]>(`/api/feedback/cached_replies`, {
    body: JSON.stringify({
      ids,
    }),
    method: "POST",
  });
}

export async function updateCachedReply(reply: Reply) {
  return makeRequest(`/api/feedback/cached_reply`, {
    body: JSON.stringify(reply),
    method: "PATCH",
  });
}

export async function getPublicSettings() {
  return await makeRequest<{ showAdminLink: boolean; adminLink: string }>("/api/settings");
}

// Questions API methods
export async function getUnansweredQuestions({
  page,
  perPage,
  storeId,
  showOnlyUnanswered = false,
  isAnswered, // для WB
  dateFrom,
  dateTo,
  lastId,
}: {
  page: number;
  perPage: number;
  storeId: string;
  showOnlyUnanswered?: boolean;
  isAnswered?: boolean; // для WB
  dateFrom?: string;
  dateTo?: string;
  lastId?: string;
}) {
  const params = new URLSearchParams({
    page: page.toString(),
    perPage: perPage.toString(),
    storeId,
  });

  // Для WB используем isAnswered, для Ozon - showOnlyUnanswered
  if (typeof isAnswered === 'boolean') {
    params.append('isAnswered', isAnswered.toString());
  } else {
    params.append('showOnlyUnanswered', showOnlyUnanswered.toString());
  }

  if (dateFrom) params.append('dateFrom', dateFrom);
  if (dateTo) params.append('dateTo', dateTo);
  if (lastId) params.append('lastId', lastId);

  return await makeRequest<QuestionResponse>(
    `/api/questions/list?${params.toString()}`
  );
}

export async function getGeneratedQuestionReply({
  questions,
  storeId,
}: {
  questions: {
    id: string;
    text: string;
    customerName: string;
    productName: string;
  }[];
  storeId: string;
}) {
  return await makeRequest<{ id: string; text: string }[]>(
    "/api/feedback/ai/generate",
    {
      method: "POST",
      body: JSON.stringify({
        feedbacks: questions.map(q => ({
          id: q.id,
          text: q.text,
          customerName: q.customerName,
          productValuation: 5, // для вопросов всегда положительный контекст
          productName: q.productName,
        })),
        storeId,
      }),
    }
  );
}

export async function publishQuestionReplies({
  replies,
  storeId,
}: {
  replies: {
    id: string;
    text: string;
    sku?: number;
  }[];
  storeId: string;
}) {
  return await makeRequest<PublishResult[]>("/api/questions/answer", {
    method: "POST",
    body: JSON.stringify({
      replies: replies.map(reply => ({
        questionId: reply.id,
        text: reply.text,
        storeId,
        sku: reply.sku,
      })),
    }),
  });
}
